import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_STATUS_DRAFT,
  LINKEDIN,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import {
  DESTINATION_URL_PLACEHOLDER,
  CALL_TO_ACTION_PLACEHOLDER,
  TITLE_PLACEHOLDER,
  HEADLINE_PLACEHOLDER,
  FIELD_REQUIRED,
  FIELD_URL_VALID,
  ADS_CONFIGURATION_DESTINATION_TITLE,
  INSTANT_FORM_PLACEHOLDER,
  UTM_PARAMETERS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import TextField from "../../../Form/TextField";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import CardContainer from "../../../Card/CardContainer";
import SelectCallToActionLinkedin from "../Ad/SelectCallToActionLinkedin";
import { REGEX_URL } from "../../../../utils/regex";
import { useDispatchApp } from "../../../../lib/redux";
import { generateAdsCampaignDescription } from "../../../../actions/adsCampaigns";
import { getIsBlockedActions } from "../../../../actions/getters";
import LeadFormContainer from "../../../LinkedIn/LeadFormContainer";
import { useFormContext } from "react-hook-form";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import {
  COPY_VALUES_IN_OTHER_ADGROUPS,
  READY,
} from "../../../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../../utils/constants";
import ChipOptional from "../../../Chip/ChipOptional";
import UtmParametersContainer from "../../../Common/Utms/UtmParametersContainer";
import { alertIfEmptyCopyValue } from "../../../../utils/copyFormValue";
import {
  commitGeneratedSectionText,
  copySectionValue,
  readStoredSectionFingerprint,
} from "../../../../utils/aiGeneratedForm";

const AssetsGroupLinkedinVideoForm = ({
  BASIC_PATH,
  control,
  errors,
  trigger,
  setValue,
  getValues,
  isDisabledFields,
  platform,
  objective,
  isPostAd = false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();
  const { watch } = useFormContext();
  const platformData = watch(`platformsData.${platform}`) || {};
  const assetGroupStatus = watch(`${BASIC_PATH}.status`);
  const isLeadsObjective = objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION;

  // Disable callToAction and leadForm if objective is LEADS and status is not DRAFT
  const isLeadsFieldsDisabled =
    isLeadsObjective && assetGroupStatus !== ADS_CAMPAIGN_STATUS_DRAFT;

  const maxLengthPrimaryText = ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[LINKEDIN];
  const maxLengthHeadline = ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[LINKEDIN];

  const onGenerateText = async ({ type, index = 0 }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );
    if (isBlocked) return;

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");
    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];

    const objective = getValues(`platformsData.${platform}.objective`);
    const response = await dispatch(
      generateAdsCampaignDescription({
        campaignID: getValues("id"),
        objective,
        platform,
        textIndex: index,
        campaignContext: getValues("context") || "",
        language: getValues("language"),
        destinationUrl:
          getValues(`${BASIC_PATH}.destinationUrls.[0].value`) || "",
        type,
        currentTexts: textsWithCurrentFirst,
        entityType: "assetsGroup",
        currentFingerprint: readStoredSectionFingerprint({
          getValues,
          basePath: BASIC_PATH,
          section: type,
        }),
      })
    );

    commitGeneratedSectionText({
      getValues,
      setValue,
      trigger,
      basePath: BASIC_PATH,
      section: type,
      index,
      platform,
      entityType: "assetsGroup",
      response,
    });
  };

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    if (alertIfEmptyCopyValue({ value, t })) return;
    const assetsGroups = getValues(`assetsGroups`);

    assetsGroups.forEach((assetsGroup, currentIndexAssetsGroup) => {
      if (assetsGroup.platform !== platform) return;

      copySectionValue({
        setValue,
        getValues,
        sourceBasePath: BASIC_PATH,
        section: type,
        platform,
        entityType: "assetsGroup",
        path: `assetsGroups.[${currentIndexAssetsGroup}].${type}`,
        value,
      });
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  return (
    <Grid container spacing={3}>
      {/* Lead Form */}
      {isLeadsObjective && (
        <Grid item xs={12}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "leadForm",
            }}
            title={
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(INSTANT_FORM_PLACEHOLDER)}
              </Typography>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <LeadFormContainer
                name={`${BASIC_PATH}.leadForm`}
                integrationID={platformData?.integrationID}
                adAccountID={platformData?.adAccountID}
                control={control}
                errors={errors}
                disabled={isDisabledFields || isLeadsFieldsDisabled}
              />
              <ClickableText
                disabled={isDisabledFields || isLeadsFieldsDisabled}
                onClick={() => onCopyValuesInOtherAds({ type: "leadForm" })}
              >
                {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
              </ClickableText>
            </Box>
          </CardContainer>
        </Grid>
      )}

      {/* Destination URL */}
      <Grid item xs={12}>
        <CardContainer
          title={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(ADS_CONFIGURATION_DESTINATION_TITLE)}
              </Typography>
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="body1">
              {t(DESTINATION_URL_PLACEHOLDER)}
            </Typography>
            <TextField
              name={`${BASIC_PATH}.destinationUrls.[0].value`}
              variant="filled"
              fullWidth
              disabled={isDisabledFields}
              control={control}
              errors={errors}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
                validate: (value) => {
                  if (!REGEX_URL.test(encodeURI(value))) {
                    return t(FIELD_URL_VALID);
                  }
                },
              }}
            />
            <ClickableText
              disabled={isDisabledFields}
              onClick={() =>
                onCopyValuesInOtherAds({ type: "destinationUrls" })
              }
            >
              {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
            </ClickableText>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mt: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body1">
                {t(UTM_PARAMETERS_PLACEHOLDER)}
              </Typography>
              <ChipOptional size="small" />
            </Box>
            <UtmParametersContainer
              name={`${BASIC_PATH}.utmParameters`}
              control={control}
              errors={errors}
              disabled={isDisabledFields}
              platform={platform}
              onCopyValuesInOtherAds={() =>
                onCopyValuesInOtherAds({ type: "utmParameters" })
              }
            />
          </Box>
        </CardContainer>
      </Grid>

      {/* Call to Action */}
      <Grid item xs={12}>
        <CardContainer
          aiGeneratedSection={{
            basePath: BASIC_PATH,
            section: "callToAction",
          }}
          title={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(CALL_TO_ACTION_PLACEHOLDER)}
              </Typography>
              <ClickableText
                disabled={isDisabledFields || isLeadsFieldsDisabled}
                onClick={() => onCopyValuesInOtherAds({ type: "callToAction" })}
              >
                {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
              </ClickableText>
            </Box>
          }
        >
          <SelectCallToActionLinkedin
            name={`${BASIC_PATH}.callToAction`}
            fullWidth
            variant="filled"
            control={control}
            errors={errors}
            disabled={isDisabledFields || isLeadsFieldsDisabled}
            objective={objective}
          />
        </CardContainer>
      </Grid>

      {/* Primary Text */}
      {!isPostAd && (
        <Grid item xs={12}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "titles",
            }}
            title={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(TITLE_PLACEHOLDER)}
                </Typography>
                <ClickableText
                  disabled={isDisabledFields}
                  onClick={() => onCopyValuesInOtherAds({ type: "titles" })}
                >
                  {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                </ClickableText>
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <TextFieldWithActions
                type="titles"
                labelTitle=""
                name={`${BASIC_PATH}.titles.[0].value`}
                disabled={isDisabledFields}
                platform={platform}
                showAddIcon={false}
                showDeleteIcon={false}
                maxLength={maxLengthPrimaryText}
                showCounterCharacters={true}
                onGenerate={async () => {
                  await onGenerateText({ type: "titles", index: 0 });
                }}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Box>
          </CardContainer>
        </Grid>
      )}

      {/* Headline */}
      {!isPostAd && (
        <Grid item xs={12}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "headlines",
            }}
            title={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(HEADLINE_PLACEHOLDER)}
                </Typography>
                <ClickableText
                  disabled={isDisabledFields}
                  onClick={() => onCopyValuesInOtherAds({ type: "headlines" })}
                >
                  {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                </ClickableText>
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <TextFieldWithActions
                type="headlines"
                labelTitle=""
                name={`${BASIC_PATH}.headlines.[0].value`}
                disabled={isDisabledFields}
                platform={platform}
                showAddIcon={false}
                showDeleteIcon={false}
                maxLength={maxLengthHeadline}
                showCounterCharacters={true}
                onGenerate={async () => {
                  await onGenerateText({ type: "headlines", index: 0 });
                }}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Box>
          </CardContainer>
        </Grid>
      )}
    </Grid>
  );
};

export default AssetsGroupLinkedinVideoForm;
